import { useQuery } from "@tanstack/react-query"
import {
  Archive,
  CalendarCheck,
  CalendarClock,
  FilePen,
  Minus,
  Percent,
  Plus,
  Wallet,
} from "lucide-react"

import { api } from "@/api/cliente"
import { type ConceptoMin, kpiConceptos } from "@/componentes/kpi/calculos"
import { FilaKpi } from "@/componentes/kpi/FilaKpi"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { comoFecha } from "@/lib/fechas"

/**
 * Los indicadores de Nómina (Administración → Nómina).
 *
 * ⚠ **Los de períodos vienen del SERVIDOR** (`/nomina/indicadores`): los netos viven en los
 * recibos, que el listado no trae, y se dan POR MONEDA. «Del mes» es por la FECHA DE PAGO
 * del período, que es cuándo sale el dinero.
 */

type IndicadoresNomina = {
  contratos_activos: number
  por_aprobar: number
  aprobados_mes: number
  neto_mes: { divisa: string; monto: string }[]
  proximo: { codigo: string; fecha_pago: string; estado: string } | null
}

export function KpiPeriodos() {
  const q = useQuery({
    queryKey: ["nomina-periodos", "indicadores"],
    queryFn: () => api.get<IndicadoresNomina>("/api/v1/nomina/indicadores"),
  })
  const k = q.data
  return (
    <FilaKpi cargando={q.isPending} error={q.isError}>
      {k && (
        <>
          <TarjetaKpi
            titulo="Por aprobar"
            detalle="Períodos en borrador"
            Icono={FilePen}
            color="var(--warning)"
            cantidad={k.por_aprobar}
          />
          <TarjetaKpi
            titulo="Aprobados del mes"
            detalle="Con sus órdenes ya redactadas"
            Icono={CalendarCheck}
            color="var(--success)"
            cantidad={k.aprobados_mes}
          />
          <TarjetaKpi
            titulo="Neto del mes"
            detalle="Recibos que se pagan este mes"
            Icono={Wallet}
            color="var(--primary)"
            cantidad={k.contratos_activos}
            montos={k.neto_mes}
            pie={`${k.contratos_activos} contratos activos`}
          />
          <TarjetaKpi
            titulo="Próximo pago"
            detalle="El período vivo más cercano"
            Icono={CalendarClock}
            color="var(--info)"
            cantidad={k.proximo ? comoFecha(k.proximo.fecha_pago) : "—"}
            pie={
              k.proximo
                ? `${k.proximo.codigo} · ${k.proximo.estado === "BORRADOR" ? "por aprobar" : "aprobado"}`
                : "Ninguno pendiente"
            }
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiConceptos() {
  // Con los archivados, aunque la lista los esconda: si no, «archivados» siempre diría cero.
  const q = useQuery({
    queryKey: ["nomina-conceptos", true],
    queryFn: () => api.get<ConceptoMin[]>("/api/v1/nomina/conceptos?incluir_archivados=true"),
  })
  const k = q.data ? kpiConceptos(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError}>
      {k && (
        <>
          <TarjetaKpi
            titulo="Asignaciones"
            detalle="Bonos y pagos que suman"
            Icono={Plus}
            color="var(--success)"
            cantidad={k.asignaciones}
          />
          <TarjetaKpi
            titulo="Deducciones"
            detalle="Lo que se descuenta"
            Icono={Minus}
            color="var(--destructive)"
            cantidad={k.deducciones}
          />
          <TarjetaKpi
            titulo="Porcentuales"
            detalle="Sobre el sueldo base"
            Icono={Percent}
            color="var(--info)"
            cantidad={k.porcentuales}
          />
          <TarjetaKpi
            titulo="Archivados"
            detalle="Explican recibos viejos"
            Icono={Archive}
            color="var(--fg-muted)"
            cantidad={k.archivados}
          />
        </>
      )}
    </FilaKpi>
  )
}
